<template>
  <div>
    <template v-if="!vertical">
      <v-stepper :alt-labels="altLabels"
                 :non-linear="nonLinear"
                 v-model="activeIndex">
        <v-stepper-header>
          <template v-for="(item, index) in items">
            <v-stepper-step :key="index"
                            :complete="retrieveHorizontalEditIcon(index) != null"
                            :edit-icon="retrieveHorizontalEditIcon(index)"
                            :editable="!isNonEditable(index)"
                            :step="showStartPoint ? index : item"
                            class="finStep"
                            @click="clickStep(index)">
              <span class="stepLabel"
                    v-if="altLabels"
                    :style="{ opacity: !isNonEditable(index) ? 1 : 0.4 }">
                {{ itemLabels[index] }}
                <template v-if="subLabels">
                  <br/>
                  <span class="stepSubLabel">
                    {{ subLabels[index] }}
                  </span>
                </template>
              </span>
            </v-stepper-step>
            <v-divider
              v-if="divider && index < items - 1"
              :key="'stepdivider' + index"
              :class="(!altLabels ? 'labelDivider' : 'noLabelDivider')"
              :style="{ opacity: !isNonEditable(index+1) ? 1 : 0.4, marginTop: $vuetify.breakpoint.mdAndUp ? '20px !important' : '0 !important'}"/>
          </template>
        </v-stepper-header>
      </v-stepper>
    </template>

    <template v-else>
      <v-stepper v-model="activeIndex"
                 :non-linear="nonLinear"
                 vertical>
        <template v-for="(item, index) in items">
          <v-stepper-step :complete="retrieveHorizontalEditIcon(index) != null"
                          :edit-icon="retrieveHorizontalEditIcon(index)"
                          :key="index"
                          :step="showStartPoint ? index : item" editable/>
          <v-stepper-content :key="'stepdivider' + index"
                             :step="showStartPoint ? index : item"
                             v-if="divider && index < items - 1"/>
        </template>
      </v-stepper>
    </template>
  </div>
</template>

<script>
export default {
  name: 'fin-numbered-dot-selector',
  props: {
    /** the selected value, starting with 0 */
    value: {
      type: Number,
      default: 0
    },
    /** the number of items to display */
    items: {
      type: Number,
      default: 3
    },
    /** array of labels */
    itemLabels: {
      type: Array
    },
    /** array of sub labels */
    subLabels: {
      type: Array
    },
    /** router navigation names */
    itemsTo: {
      type: Array
    },
    /** if set to `true` the labels are shown if the `itemLabels` match the `items` size */
    showItemLabels: {
      type: Boolean,
      default: false
    },
    /** if set to `true`the first element (step 0) is displayed as a circle (mdi-circle), otherwise it starts at 1 */
    showStartPoint: {
      type: Boolean,
      default: false
    },
    /** if set to `true` all steps are selectable */
    nonLinear: {
      type: Boolean,
      default: false
    },
    /** if set to `true` the steps are shown vertically, otherwise horizontally */
    vertical: {
      type: Boolean,
      default: false
    },
    /** if set to `true` a line is drawn between each step */
    divider: {
      type: Boolean,
      default: false
    },
    /** mark all items up to (but not including) the current item as done (check icon) */
    markAsDone: {
      type: Boolean,
      default: false
    },
    /** if set to `true` the numbered dots aren't clickable / selectable */
    nonEditable: {
      type: [Boolean, Array, Number],
      default: false
    },
    /** if set to `true` the numbered dots are successively clickable / selectable. To be used with nonEditable: true */
    stepsReleasedSuccessively: {
      type: Boolean,
      default: false
    },
    done: {
      type: Boolean
    }
  },
  data() {
    return {
      activeIndex: this.value
    }
  },
  computed: {
    altLabels() {
      return this.showItemLabels && this.itemLabels && this.itemLabels.length === this.items && this.$vuetify.breakpoint.mdAndUp
    }
  },
  methods: {
    retrieveHorizontalEditIcon(index) {
      let result = null
      if (this.markAsDone) {
        if (!this.showStartPoint) {
          if (index + 1 < this.value || this.done) {
            result = '$complete'
          }
        } else {
          if (index === 0 && this.value === 0) {
            result = 'mdi-circle'
          } else if (index < this.value || this.done) {
            result = '$complete'
          }
        }
      }

      if (!this.markAsDone) {
        if (this.showStartPoint) {
          if (index === 0 && this.value === 0) {
            result = 'mdi-circle'
          } else if (index === 0 && this.value > 0) {
            result = 'mdi-circle-outline'
          }
        }
      }

      return result
    },
    isNonEditable(index) {
      if (this.nonEditable === 0 || this.nonEditable) {
        if (Array.isArray(this.nonEditable)) {
          return this.nonEditable.indexOf(index) >= 0
        } else if (Number.isInteger(this.nonEditable)) {
          return index >= this.nonEditable
        }
        return true
      }
      return false
    },
    clickStep(currStepElem) {
      if (!this.isNonEditable(currStepElem) && !!this.itemsTo && this.itemsTo.length === this.items) {
        if (this.value !== currStepElem + 1) {
          this.$router.push({name: this.itemsTo[currStepElem]})
        }
      }
    }
  },
  watch: {
    // workaround v-stepper is losing the selection while switching from horizontal to vertical display
    vertical() {
      const old = this.activeIndex
      this.activeIndex = -1
      this.$nextTick(() => this.activeIndex = old)
    },
    value(val) {
      this.activeIndex = val
    },
    activeIndex(val) {
      // to prevent emitting workaround
      if (val !== -1) {
        this.$emit('input', val)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../../styles/variables.scss";

.theme--light.v-stepper,
.theme--dark.v-stepper {
  box-shadow: none !important;
  background-color: transparent;
}

::v-deep .v-stepper__step {
  background: none !important;
}

::v-deep .v-stepper__step .v-ripple__container {
  display: none;
}

::v-deep .v-stepper__step .v-stepper__step__step {
  font-size: 18px;
  font-weight: bold;
  width: 32px;
  height: 32px;
  background-color: white !important;
  color: $fin-color-primary-dark !important;
  border-color: $fin-color-primary-dark !important;
  border: 2px solid;
}

::v-deep .v-stepper__step.v-stepper__step--inactive:not(.v-stepper__step--editable) .v-stepper__step__step {
  opacity: 0.3;
}

::v-deep .v-stepper__step .v-stepper__step__step.primary {
  background-color: $fin-color-primary-dark !important;
  color: white !important;
}

::v-deep .v-divider {
  border: 1px solid $fin-color-primary-dark !important;
}

::v-deep .v-stepper__content {
  margin: -16px -36px -26px 39px !important;
}

::v-deep .v-stepper__content {
  border-left: 2px solid $fin-color-primary-dark !important;
}

::v-deep .v-stepper--alt-labels .v-stepper__step {
  padding: 5px;
}

::v-deep .v-stepper__step--complete .v-stepper__step__step.primary {
  background-color: transparent !important;
}

::v-deep .v-stepper__step--complete .v-icon {
  color: $fin-color-primary-dark !important;
}

.noLabelDivider {
  margin-left: -32px;
  margin-right: -26px;
}

.stepLabel {
  font-family: $body-font-family;
  font-size: 14px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  //line-height: 1.39;
  letter-spacing: normal;
  text-align: center;
  color: $fin-color-primary-dark;
}

.stepSubLabel {
  color: $fin-color-primary-dark;
  font-family: $body-font-family;
  font-size: 12px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  //line-height: normal;
  letter-spacing: normal;
  text-align: center;
}

.stepLabel, .stepSubLabel {
  text-shadow: none;
}
</style>
